// Single source of truth for /tools/mortgage-ready-checklist.

export const PAGE_PATH = "/tools/mortgage-ready-checklist";
export const PAGE_URL = "https://www.homeapproach.com/tools/mortgage-ready-checklist";
export const PAGE_TITLE = "Mortgage-Ready Checklist";
export const PAGE_DESCRIPTION =
  "Every box to tick before you apply: a 36% DTI target, a 620+ conventional baseline, 2–5% closing costs, cash reserves, and the documents and gift letters underwriting will ask for.";

export type ChecklistItem = {
  id: string;
  text: string;
  detail?: string;
  tool?: { label: string; to: string; type: string };
};

export type ChecklistSection = {
  id: string;
  title: string;
  buyerStage: string;
  intro: string;
  items: ChecklistItem[];
};

export const SECTIONS: ChecklistSection[] = [
  {
    id: "financial-health",
    title: "Financial Health",
    buyerStage: "budgeting",
    intro:
      "Underwriting starts with the ratio between what you earn and what you already owe. Getting this right before you apply changes the size of the loan, the rate, and whether an approval survives to closing.",
    items: [
      {
        id: "dti",
        text: "Get total monthly debt payments plus the new housing payment to about 36% of gross income.",
        detail:
          "36% is the comfortable target. Many programs still approve at 43%, and some automated approvals stretch to 50% with strong credit and reserves — but the higher you go, the thinner the file and the tighter the budget after closing.",
        tool: { label: "Calculate what you can actually afford", to: "/tools/real-affordability", type: "calculator" },
      },
      {
        id: "revolving",
        text: "Pay down credit cards and lines of credit before anything else.",
        detail:
          "A revolving balance hits twice: it raises DTI and lowers your score through utilization. Paying $300 off a card can free more borrowing power than $300 toward the down payment.",
      },
      {
        id: "income-stability",
        text: "Line up two years of stable, documentable income.",
        detail:
          "Job changes inside the same field are usually fine. Moving from W-2 to self-employment right before applying is not — that typically restarts a two-year clock.",
      },
      {
        id: "no-new-debt",
        text: "Stop financing anything new — no car loans, no furniture, no buy-now-pay-later.",
        detail:
          "A new payment can knock you out of qualifying ratios weeks after you were approved. Lenders re-pull credit before closing.",
      },
      {
        id: "readiness-score",
        text: "Score your overall readiness so you know which factor is holding you back.",
        tool: { label: "Run the readiness score", to: "/tools/homebuyer-readiness-score", type: "calculator" },
      },
    ],
  },
  {
    id: "credit",
    title: "Credit",
    buyerStage: "credit_cleanup",
    intro:
      "Mortgage lenders pull older FICO versions (2, 4, and 5) from all three bureaus and use the middle score of the lowest borrower. That number sets your rate and your mortgage insurance cost.",
    items: [
      {
        id: "baseline",
        text: "Reach a 620 middle score for conventional financing.",
        detail:
          "580 is the common FHA floor with 3.5% down, and 500–579 may work with 10% down. Pricing improves in tiers — 660, 700, 740 — so a small climb before applying can be worth real money.",
      },
      {
        id: "pull-reports",
        text: "Pull all three reports and dispute anything inaccurate at least 90 days out.",
        detail:
          "Disputes take 30–45 days to resolve, and an unresolved dispute flag can stall underwriting on its own.",
        tool: { label: "Check your score", to: "/check-my-score", type: "credit" },
      },
      {
        id: "utilization",
        text: "Get reported utilization under 10% on the statement that lands before you apply.",
        detail:
          "Balances report on the statement date, not the due date. Paying early is what moves the score.",
      },
      {
        id: "leave-alone",
        text: "Do not close old accounts, open new ones, or let anyone run hard pulls.",
        detail:
          "Closing your oldest card shortens your history and raises utilization. In the six months before applying, boring is the strategy.",
        tool: { label: "See the 30/60/90-day credit plan", to: "/tools/raise-credit-score-before-mortgage", type: "guide" },
      },
    ],
  },
  {
    id: "savings",
    title: "Savings",
    buyerStage: "saving",
    intro:
      "The down payment is the number everyone talks about. Closing costs and reserves are what actually surprise people at the table.",
    items: [
      {
        id: "down-payment",
        text: "Know your real down payment target — 3%, 3.5%, 5%, or 20%.",
        detail:
          "20% avoids mortgage insurance, but low-down-payment loans exist for a reason. Compare the cost of waiting to save against the cost of PMI you can later remove.",
        tool: { label: "Plan your down payment", to: "/down-payment-planner", type: "planner" },
      },
      {
        id: "closing-costs",
        text: "Set aside 2% to 5% of the purchase price for closing costs.",
        detail:
          "Lender fees, title, appraisal, recording, prepaid taxes and insurance, and escrow funding. On a $350,000 home that is roughly $7,000 to $17,500 on top of the down payment.",
        tool: { label: "See closing costs in your state", to: "/closing-costs", type: "calculator" },
      },
      {
        id: "reserves",
        text: "Keep two to six months of full housing payments in reserve after closing.",
        detail:
          "Some loan programs require reserves outright. Every program treats them as a compensating factor, and every household needs them the first year.",
      },
      {
        id: "seasoning",
        text: "Season your funds for 60 days and be ready to source any large deposit.",
        detail:
          "Anything unusual relative to your income gets a letter of explanation and a paper trail. Cash you cannot document usually cannot be used.",
      },
      {
        id: "gift-letter",
        text: "If family is helping, get a signed gift letter and the donor's transfer record.",
        detail:
          "The letter must state the amount, the relationship, and that no repayment is expected. A gift that looks like a loan becomes a debt in the ratio.",
      },
      {
        id: "assistance",
        text: "Check whether you qualify for down payment assistance before you spend your own cash.",
        tool: { label: "Find grants and assistance", to: "/grant-finder", type: "grants" },
      },
    ],
  },
  {
    id: "documents",
    title: "Documents",
    buyerStage: "document_prep",
    intro:
      "Nearly every delayed closing traces back to a document that arrived late. Gather these into one folder before you talk to a lender and underwriting moves at a completely different speed.",
    items: [
      { id: "paystubs", text: "Pay stubs covering the most recent 30 days." },
      { id: "w2s", text: "W-2s for the last two years — every employer, every year." },
      {
        id: "returns",
        text: "Federal tax returns for the last two years, all pages and all schedules.",
        detail: "Partial returns get sent back. Include K-1s and business returns if you own 25% or more of a company.",
      },
      {
        id: "statements",
        text: "Two to three months of statements for every account holding funds you'll use.",
        detail:
          "All pages, including the intentionally blank ones. Screenshots and partial PDFs are routinely rejected.",
      },
      { id: "id", text: "Government-issued photo ID and Social Security number for each borrower." },
      {
        id: "self-employed",
        text: "Self-employed: add a year-to-date profit and loss, business bank statements, and 1099s.",
        detail:
          "Income is generally averaged over two years of net (after write-offs) figures, which is why aggressive deductions can shrink the loan you qualify for.",
      },
      {
        id: "other-docs",
        text: "Add anything that explains your file: divorce decree, child support, bankruptcy discharge, rental leases, VA Certificate of Eligibility.",
      },
    ],
  },
  {
    id: "final-steps",
    title: "Final Steps",
    buyerStage: "pre_approval",
    intro:
      "The last stretch is about turning a prepared file into a real approval — and then not breaking it before closing.",
    items: [
      {
        id: "preapproval",
        text: "Get a verified pre-approval, not a pre-qualification.",
        detail:
          "A pre-qualification is an estimate off numbers you stated. A pre-approval means a lender pulled credit and reviewed documents. Sellers can tell the difference, and in a competitive market only one of them gets taken seriously.",
      },
      {
        id: "rate-shop",
        text: "Shop three to five lenders inside a 14-day window.",
        detail:
          "Mortgage inquiries in a short window count as one event for scoring purposes. Compare written Loan Estimates side by side, not verbal rates.",
        tool: { label: "Compare lender quotes", to: "/tools/compare-lender-quotes", type: "guide" },
      },
      {
        id: "questions",
        text: "Bring a list of questions to every loan officer call.",
        tool: { label: "Loan officer question checklist", to: "/tools/loan-officer-questions", type: "checklist" },
      },
      {
        id: "freeze",
        text: "Between approval and closing: change nothing.",
        detail:
          "No job changes, no new credit, no large unexplained deposits, no moving money between accounts without a paper trail.",
      },
      {
        id: "timeline",
        text: "Know what happens after the offer so nothing catches you off guard.",
        tool: { label: "See the full home buying timeline", to: "/tools/home-buying-timeline", type: "guide" },
      },
    ],
  },
];

export type Faq = { id: string; q: string; a: string };

export const FAQS: Faq[] = [
  {
    id: "first-time",
    q: "What does a first-time buyer need to be mortgage ready?",
    a: "The same four pillars as any buyer, with more help available: a middle credit score of at least 620 for conventional financing (580 for typical FHA), total debt payments near 36% of gross income, enough cash for the down payment plus 2% to 5% in closing costs, and two years of documented income. First-time buyers should also check state and local down payment assistance before spending their own savings — many programs cover part or all of the down payment and closing costs for buyers under an income cap.",
  },
  {
    id: "self-employed",
    q: "What do self-employed borrowers need for a mortgage?",
    a: "Two years of complete personal federal tax returns and, if you own 25% or more of a business, two years of business returns, plus a year-to-date profit and loss statement, business bank statements, and any 1099s. Lenders average your net income after write-offs over those two years, so heavy deductions reduce the loan you qualify for even when cash flow is strong. Bank-statement loan programs exist as an alternative but usually carry higher rates.",
  },
  {
    id: "preapproval-vs-prequal",
    q: "What is the difference between pre-qualification and pre-approval?",
    a: "A pre-qualification is an informal estimate based on numbers you tell the lender, with no verification, and it can be issued in minutes. A pre-approval means the lender pulled your credit, reviewed your income and asset documents, and issued a conditional commitment for a specific amount. Sellers and agents treat a pre-approval as a real offer; a pre-qualification often gets set aside in a multiple-offer situation.",
  },
  {
    id: "large-deposits",
    q: "Do I have to explain large deposits in my bank account?",
    a: "Yes. Any deposit that is large relative to your regular income triggers a request to source it — typically a letter of explanation plus documentation showing where the money came from. Payroll, tax refunds, and documented transfers between your own accounts are straightforward. Cash deposits and undocumented transfers usually cannot be counted toward your down payment or reserves at all, so deposit any gift or windfall at least 60 days before applying.",
  },
  {
    id: "how-many-statements",
    q: "How many bank statements do lenders need?",
    a: "Two to three months for every account holding funds you plan to use, and every page of each statement, including pages that appear blank. Lenders use them to verify your down payment and reserves, confirm funds have been seasoned in your account, and spot undisclosed debt payments or unexplained deposits.",
  },
  {
    id: "how-long",
    q: "How long does it take to become mortgage ready?",
    a: "It depends on which pillar is weakest. Document gathering takes a weekend. Credit improvements from paying down utilization can show in 30 to 60 days, while removing the effect of a late payment or collection takes longer. Building a down payment plus closing costs and reserves is usually the longest stretch — commonly 12 to 24 months of deliberate saving, less if you qualify for assistance.",
  },
];

export const DISCLAIMER =
  "This checklist is educational guidance, not lending, tax, or legal advice, and it is not a loan offer, pre-qualification, or commitment to lend. Credit score minimums, debt ratio limits, reserve requirements, and document lists vary by lender, loan program, and your individual file. Confirm your requirements with a licensed loan officer.";

const SITE = "https://www.homeapproach.com";

/** WebPage + BreadcrumbList + FAQPage, baked into the server HTML via routeMeta. */
export const MORTGAGE_READY_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export const TOTAL_ITEMS = SECTIONS.reduce((n, s) => n + s.items.length, 0);
